/**
 * ReceiveHistoryRow — BLU-3xx Phase 47.01.2-B-05
 *
 * Single row of the Receive History table. Mirrors the B-03 owner-column +
 * brand-purple-ribbon pattern from `InventoryTableList.tsx` for cross-org
 * provenance — when the table is unfiltered (multi-org) AND the row's owner
 * is NOT the caller, the row gets a left-edge ribbon and the owner cell
 * uses `text-threadheads-purple` to underline the cross-org case.
 *
 * Reverse button styling per CONTEXT.md FE-8:
 *   - Default: ghost variant, neutral text. The history page is a
 *     transparency surface — rows of red-bordered buttons would read as
 *     "danger zone."
 *   - Hover/focus: shifts to a destructive-tinted ring + text. Reserve the
 *     loud destructive-fill styling for the AlertDialog's confirm button.
 *
 * The Date cell shows a relative label (`formatDistanceToNow`) for fast
 * scanning; the absolute timestamp is on a tooltip for forensic detail.
 */
import { format, formatDistanceToNow } from 'date-fns';
import { Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ClientOrgBadge } from '@/components/ClientOrgBadge';

/** Shape of a receipt row consumed by the Receive History table + dialog.
 *  Mirrors the service-layer `listRecentReceipts` return shape (a subset:
 *  the page only renders a handful of columns). Lives in this file so the
 *  Row can stand alone in Task 1 (the AlertDialog lands in Task 2). */
export interface ReceiveHistoryReceipt {
  id: string;
  ownerOrganizationId: string;
  actorOrganizationId: string | null;
  sku: string | null;
  skuTitle: string | null;
  skuColor: string | null;
  skuSize: string | null;
  quantityChange: number;
  createdAt: string;
}

interface ReceiveHistoryRowProps {
  receipt: ReceiveHistoryReceipt;
  showOwnerColumn: boolean;
  showActorColumn: boolean;
  ownOrgId?: string;
  ownerName: string;
  actorName: string | null;
  onReverseClick: (receipt: ReceiveHistoryReceipt) => void;
  /** When true, hides the Reverse button and renders an em-dash placeholder.
   *  Used while a reversal mutation for THIS row is in flight (parent
   *  controls); the row will disappear after invalidation. */
  isPending?: boolean;
}

export function ReceiveHistoryRow({
  receipt,
  showOwnerColumn,
  showActorColumn,
  ownOrgId,
  ownerName,
  actorName,
  onReverseClick,
  isPending = false,
}: ReceiveHistoryRowProps) {
  const { t } = useTranslation('inventory');
  const isClientOwned =
    showOwnerColumn &&
    !!ownOrgId &&
    receipt.ownerOrganizationId !== ownOrgId;

  const createdAt = new Date(receipt.createdAt);
  const variant = [receipt.skuColor, receipt.skuSize].filter(Boolean).join(' / ');
  const reverseLabel = t('receiveHistory.reverseAriaLabel');

  return (
    <TableRow
      className={isClientOwned ? 'border-l-4 border-l-threadheads-purple/30' : ''}
    >
      <TableCell className="text-sm whitespace-nowrap">
        {/* TooltipProvider is hoisted to the parent ReceiveHistoryTable
            (BLU-3xx Phase 47.01.2-B-fix MED-7) so we don't instantiate
            one per row. The Tooltip below is a child of that single
            shared provider. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-default">
              {formatDistanceToNow(createdAt, { addSuffix: true })}
            </span>
          </TooltipTrigger>
          <TooltipContent>{format(createdAt, 'PPpp')}</TooltipContent>
        </Tooltip>
      </TableCell>

      <TableCell className="font-mono text-sm">
        <div className="flex flex-col">
          <span>{receipt.sku ?? '-'}</span>
          {receipt.skuTitle && (
            <span className="font-sans text-xs text-muted-foreground truncate max-w-[280px]">
              {receipt.skuTitle}
            </span>
          )}
          {variant && (
            <span className="font-sans text-xs text-muted-foreground">
              {variant}
            </span>
          )}
        </div>
      </TableCell>

      <TableCell className="text-right tabular-nums font-medium">
        +{receipt.quantityChange}
      </TableCell>

      {showOwnerColumn && (
        <TableCell>
          {/* BLU-3xx Phase 47.01.2-B-fix3.5 (BUG 5b): use ClientOrgBadge
              for cross-org owner cells so the visual treatment matches
              other cross-org surfaces (orders queue, station lists). */}
          {isClientOwned ? (
            <ClientOrgBadge
              orgName={ownerName}
              className="max-w-[160px] truncate"
            />
          ) : (
            <span className="text-xs text-muted-foreground truncate inline-block max-w-[160px]">
              {ownerName}
            </span>
          )}
        </TableCell>
      )}

      {showActorColumn && (
        <TableCell>
          <span className="text-xs text-muted-foreground truncate inline-block max-w-[160px]">
            {actorName ?? '-'}
          </span>
        </TableCell>
      )}

      <TableCell className="text-right">
        {isPending ? (
          <span className="text-xs text-muted-foreground">
            {t('receiveUndo.reversing')}
          </span>
        ) : (
          // BLU-3xx Phase 47.01.2-B-fix MED-11: icon-only Reverse
          // trigger. The AlertDialog already carries the verbose
          // "Reverse receipt" CTA; the per-row trigger is just an
          // affordance, not a confirmation. Tooltip + aria-label
          // surface the verbose label to keyboard / screen-reader
          // users.
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors duration-200 motion-reduce:transition-none"
                onClick={() => onReverseClick(receipt)}
                aria-label={reverseLabel}
              >
                <Undo2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{reverseLabel}</TooltipContent>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
}
